import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import { Inline, Stack } from "components/common/Stack";
import { PersonPicker, type Person } from "components/common/PersonPicker";
import { TextInput } from "components/common/TextInput";
import { SectionRule } from "components/common/SectionRule";
import { Badge } from "components/common/Badge";
import { Callout } from "components/common/Callout";
import { Button } from "components/common/Button";
import { Fold } from "components/common/Fold";
import { TooltipButton } from "components/common/TooltipButton";
import { Tooltip } from "@/components/common/Tooltip";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { resolveDatasetColor } from "@/lib/datasetColor";
import { purgeDate } from "@/lib/deletionGrace";
import {
  flattenSpaceTree,
  spaceColor,
  useOrgContextStore,
  type SpacePath,
} from "@/components/main/stores/orgContextStore";
import {
  fetchOrgSpaceAccess,
  fetchOrgSpaces,
  grantSpaceRole,
  isAbandoned,
  isTrashedRoot,
  restoreSpace,
  type Org,
  type OrgMember,
  type OrgSpaceAccess,
  type Space,
  type SpaceGrant,
} from "@/lib/api/organizations";

const roleLabel = (role: string) =>
  role.charAt(0).toUpperCase() + role.slice(1);

/// The ADDRESS identifies a person; the display name does not (two members here
/// were both "Maximilian Schuberth"), so every tooltip carries both.
const holderAddress = (holder: SpaceGrant) =>
  holder.email ?? holder.subject.replace(/^user:/, "");

const holderLine = (holder: SpaceGrant) =>
  holder.name
    ? `${holder.name} · ${holderAddress(holder)}`
    : holderAddress(holder);

/// How a person reaches one space: the role they hold, and the ancestor it sits
/// on when they hold nothing here directly.
interface Reach {
  role: string;
  via: string | null;
}

/// Mirrors the server's manager walk (`unmanaged_spaces`): a space inherits its
/// parent's access only while it is Open, so the search stops at the first space
/// that severs inheritance. Holders are per-space tuples — direct grants only —
/// so without this walk "reached by" missed every inherited space.
const reachOf = (
  spaceId: string,
  person: Person,
  byId: Map<string, OrgSpaceAccess>,
  spaceById: Map<string, Space>,
): Reach | null => {
  const seen = new Set<string>();
  let current: string | null = spaceId;
  let hops = 0;
  while (current !== null && !seen.has(current)) {
    seen.add(current);
    const space = spaceById.get(current);
    if (!space) return null;
    const holder = (byId.get(current)?.holders ?? []).find(
      (h) => h.email === person.email,
    );
    if (holder) {
      return { role: holder.role, via: hops === 0 ? null : space.name };
    }
    if (space.classification !== "open") return null;
    current = space.parent_space_id ?? null;
    hops += 1;
  }
  return null;
};

/// Governance, not a working surface (D3): the whole org's space tree, whether
/// the caller holds anything on it or not. The rail deliberately shows only what
/// you hold, so this is where a governor finds a space that is nobody's — and
/// the self-grant beside it is the only way in, audited.
///
/// Nothing is listed until asked for. An org's whole space tree is not an
/// answer to any question a governor arrives with; they come looking for one
/// space, one person's reach, or the orphans.
export const SpacesPanel: React.FC<{
  org: Org;
  members: OrgMember[];
  /// Fired when a space gains a manager — the menu's orphan count is the
  /// parent's, and appointing one here is what makes it stale.
  onSpacesChanged?: () => void;
}> = ({ org, members, onSpacesChanged }) => {
  const navigate = useNavigate();
  const { refreshSpaces, activeOrgId, switchOrg } = useOrgContextStore(
    useShallow((s) => ({
      refreshSpaces: s.refreshSpaces,
      activeOrgId: s.activeOrgId,
      switchOrg: s.switchOrg,
    })),
  );
  // Space settings reads the ACTIVE org's spaces, and these settings may be another org's.
  const openSpace = async (spaceId: string) => {
    if (activeOrgId !== org.org_id) await switchOrg(org.org_id);
    navigate(`/spaces/${spaceId}`);
  };

  const [access, setAccess] = useState<OrgSpaceAccess[] | null>(null);
  // Read for THIS org rather than the store's list: that one is a snapshot
  // taken at the last org switch, so a space someone else created since simply
  // did not exist to the one surface whose job is to inventory the whole org.
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [loading, setLoading] = useState(false);
  const [spaceQuery, setSpaceQuery] = useState("");
  const [person, setPerson] = useState<Person | null>(null);
  const [orphansOpen, setOrphansOpen] = useState(false);
  const [doomedOpen, setDoomedOpen] = useState(false);
  const [granting, setGranting] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);

  const asking = spaceQuery.trim() !== "" || person !== null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [holders, tree] = await Promise.all([
        fetchOrgSpaceAccess(org.org_id),
        fetchOrgSpaces(org.org_id),
      ]);
      setAccess(holders);
      setSpaces(tree);
    } finally {
      setLoading(false);
    }
  }, [org.org_id]);

  /// Fetch on the first question asked, not on mount, and reuse the answer for
  /// the rest of the session's filtering — the filters are client-side, so a
  /// request per keystroke would buy nothing.
  const ensureLoaded = useCallback(() => {
    if (access === null && !loading) void load();
  }, [access, loading, load]);

  // The tree is a plain list and the orphan warning has to be there before any
  // question is asked; only the access walk above is worth deferring.
  useEffect(() => {
    void fetchOrgSpaces(org.org_id)
      .then(setSpaces)
      .catch(() => setSpaces([]));
  }, [org.org_id]);

  const orphanCount = useMemo(
    () => spaces.filter(isAbandoned).length,
    [spaces],
  );

  const byId = useMemo(
    () => new Map((access ?? []).map((a) => [a.space_id, a])),
    [access],
  );
  const paths = useMemo(() => flattenSpaceTree(spaces), [spaces]);
  const spaceById = useMemo(
    () => new Map(spaces.map((sp) => [sp.space_id, sp])),
    [spaces],
  );
  const directory = useMemo<Person[]>(
    () => members.map((m) => ({ email: m.email, displayName: m.display_name })),
    [members],
  );

  const visible = useMemo(() => {
    if (!asking || access === null) return [];
    const needle = spaceQuery.trim().toLowerCase();
    const rows: { path: SpacePath; reach: Reach | null }[] = [];
    for (const path of paths) {
      if (needle && !path.space.name.toLowerCase().includes(needle)) continue;
      const reach = person
        ? reachOf(path.space.space_id, person, byId, spaceById)
        : null;
      if (person && !reach) continue;
      rows.push({ path, reach });
    }
    return rows;
  }, [asking, access, paths, byId, spaceById, spaceQuery, person]);

  const orphanPaths = useMemo(
    () => paths.filter((p) => isAbandoned(p.space)),
    [paths],
  );
  // D53: a doomed space is hidden from every working surface, and the Trash
  // groups it for its managers only — this is where a governor learns of it.
  // Roots only: a doomed child goes, and comes back, with its parent.
  const doomedPaths = useMemo(
    () => paths.filter((p) => isTrashedRoot(p.space, spaceById)),
    [paths, spaceById],
  );

  const selfId = useMemo(() => localStorage.getItem("clientID") ?? "", []);
  const holdsDirectly = useCallback(
    (spaceId: string) =>
      !!byId.get(spaceId)?.holders.some((h) => h.subject === `user:${selfId}`),
    [byId, selfId],
  );

  // Break glass on the ROOT only: an Open space decides no access of its own
  // (D50), so a grant one level down has nowhere to land — and the act's real
  // scope is the subtree anyway, which is what the root's row says.
  const grantSelf = async (spaceId: string) => {
    if (!selfId) {
      showNotificationGlobal("Account ID unavailable", "error");
      return;
    }
    setGranting(spaceId);
    try {
      await grantSpaceRole(spaceId, { user: selfId }, "manager");
      await load();
      // The rail reads `governed_only`, which the grant has just flipped.
      await refreshSpaces();
      onSpacesChanged?.();
    } catch {
      showNotificationGlobal("Grant failed", "error");
    } finally {
      setGranting(null);
    }
  };

  const cancelDeletion = async (space: Space) => {
    setCancelling(space.space_id);
    try {
      const { models_restored } = await restoreSpace(space.space_id);
      showNotificationGlobal(
        models_restored > 0
          ? `${space.name} restored with ${models_restored} model${models_restored === 1 ? "" : "s"}`
          : `${space.name} restored`,
        "success",
      );
      setSpaces(await fetchOrgSpaces(org.org_id));
      await refreshSpaces();
      onSpacesChanged?.();
    } catch (error) {
      showNotificationGlobal(
        error instanceof Error
          ? error.message
          : "Could not cancel the deletion",
        "error",
      );
    } finally {
      setCancelling(null);
    }
  };

  const askByPerson = (picked: Person | null) => {
    setPerson(picked);
    ensureLoaded();
  };
  const toggleOrphans = () => {
    setOrphansOpen((open) => !open);
    ensureLoaded();
  };
  const toggleDoomed = () => {
    setDoomedOpen((open) => !open);
    ensureLoaded();
  };
  const row = (path: SpacePath, reach: Reach | null) => (
    <SpaceAccessRow
      key={path.space.space_id}
      path={path}
      entry={byId.get(path.space.space_id)}
      reach={reach}
      canGrantSelf={
        !holdsDirectly(path.space.space_id) &&
        path.space.classification !== "open"
      }
      isGranting={granting === path.space.space_id}
      onGrantSelf={() => void grantSelf(path.space.space_id)}
      isCancelling={cancelling === path.space.space_id}
      onCancelDeletion={() => void cancelDeletion(path.space)}
      onOpen={() => void openSpace(path.space.space_id)}
    />
  );

  return (
    <section className="flex flex-col">
      <SectionRule
        label="Spaces"
        trailing={asking && access !== null ? `${visible.length}` : undefined}
      />
      <Stack gap="md">
        <span className="text-fg-muted-regular-small">
          Every space in this organization, including those you hold no grant
          on. Self-granted manager access is recorded in the governance log.
        </span>

        <Inline gap="sm" align="center">
          <TextInput
            value={spaceQuery}
            onChange={(v) => {
              setSpaceQuery(v);
              ensureLoaded();
            }}
            placeholder="Find a space"
            ariaLabel="Find a space"
            // `input-custom` is w-full, so without an explicit basis this input
            // claims the row and collapses its siblings. TextInput's className
            // REPLACES the default, so the base class has to be repeated.
            className="input-custom-sm min-w-0 flex-1"
          />
          <PersonPicker
            directory={directory}
            selected={person}
            onSelect={askByPerson}
            placeholder="Reached by…"
            ariaLabel="Filter by person"
            className="min-w-0 flex-1"
          />
        </Inline>

        {orphanCount > 0 && (
          <FlaggedSpaces
            title={`${orphanCount} ${orphanCount === 1 ? "space has" : "spaces have"} no manager`}
            body="Nobody owns the models inside them. Open a space to appoint a manager."
            subject="spaces with no manager"
            open={orphansOpen}
            onToggle={toggleOrphans}
            loading={access === null || loading}
          >
            {orphanPaths.map((path) => row(path, null))}
          </FlaggedSpaces>
        )}

        {doomedPaths.length > 0 && (
          <FlaggedSpaces
            title={`${doomedPaths.length} ${doomedPaths.length === 1 ? "space is" : "spaces are"} in the Trash`}
            body="Everything inside them is in the Trash too. Restore a space to bring it all back."
            subject="spaces in the Trash"
            open={doomedOpen}
            onToggle={toggleDoomed}
            loading={access === null || loading}
          >
            {doomedPaths.map((path) => row(path, null))}
          </FlaggedSpaces>
        )}

        {!asking ? (
          <span className="py-2 text-fg-muted-regular-small">
            Search by space name, or list the spaces a member can reach.
          </span>
        ) : loading || access === null ? (
          <span className="py-2 text-fg-muted-regular-small">Loading…</span>
        ) : visible.length === 0 ? (
          <span className="py-2 text-fg-muted-regular-small">
            No space matches.
          </span>
        ) : (
          <Stack gap="2xs">
            <Inline
              gap="sm"
              align="center"
              className="px-2 text-fg-muted-regular-small"
            >
              <span className="min-w-0 flex-1">Space</span>
              {/* Asked about a person, the column answers about that person:
                  "Managed by" named someone else entirely, so the reply to
                  "where does she reach" said nothing about her. */}
              <span className="shrink-0">
                {person ? "Holds" : "Managed by"}
              </span>
            </Inline>
            <div className="flex max-h-96 flex-col overflow-y-auto">
              {visible.map(({ path, reach }) => row(path, reach))}
            </div>
          </Stack>
        )}
      </Stack>
    </section>
  );
};

/// A warning with a count and the rows behind it, listed only on request —
/// the warning is the answer, the rows are for acting on it, inside it.
const FlaggedSpaces: React.FC<{
  title: string;
  body: string;
  /// Tells the two callouts' toggles apart for a screen reader.
  subject: string;
  open: boolean;
  onToggle: () => void;
  loading: boolean;
  children: React.ReactNode;
}> = ({ title, body, subject, open, onToggle, loading, children }) => (
  <Callout variant="warning" title={title}>
    <span className="flex flex-col items-start gap-1">
      {body}
      <Button
        variant="quiet-link"
        size="sm"
        // The callout's own tone, not the accent of an ordinary link.
        className="hover:text-warning! focus-visible:text-warning! active:text-warning!"
        aria-label={open ? `Hide ${subject}` : `Show ${subject}`}
        aria-expanded={open}
        onClick={onToggle}
      >
        {open ? "Hide spaces" : "Show spaces"}
      </Button>
    </span>
    <Fold open={open}>
      {loading ? (
        <span className="text-fg-muted-regular-small">Loading…</span>
      ) : (
        <div className="flex max-h-96 flex-col overflow-y-auto pt-1">
          {children}
        </div>
      )}
    </Fold>
  </Callout>
);

const SpaceAccessRow: React.FC<{
  path: SpacePath;
  entry: OrgSpaceAccess | undefined;
  /// Set only while a person filter is on: what THEY hold here.
  reach: Reach | null;
  canGrantSelf: boolean;
  isGranting: boolean;
  onGrantSelf: () => void;
  isCancelling: boolean;
  onCancelDeletion: () => void;
  onOpen: () => void;
}> = ({
  path,
  entry,
  reach,
  canGrantSelf,
  isGranting,
  onGrantSelf,
  isCancelling,
  onCancelDeletion,
  onOpen,
}) => {
  const { space, ancestors } = path;
  const doomed = !!space.deletion_scheduled_at;
  const holders = entry?.holders ?? [];
  const managers = holders.filter((h) => h.role === "manager");
  const managedByTooltip =
    managers.length > 0
      ? `Managed by\n${managers.map(holderLine).join("\n")}`
      : "No manager on this space";

  return (
    // The name is the keyboard's way in; a click anywhere on the row opens the space.
    <div
      className="group flex cursor-pointer items-center gap-2 rounded-app px-2 item-height hover:bg-list-hover"
      onClick={onOpen}
    >
      <Tooltip title={`Space colour\n${space.name}`}>
        <span
          aria-hidden
          className="h-2 w-2 shrink-0 rounded-app"
          style={{ backgroundColor: resolveDatasetColor(spaceColor(space)) }}
        />
      </Tooltip>
      <button
        type="button"
        aria-label={`Open settings for ${space.name}`}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 text-left"
      >
        {ancestors.length > 0 && (
          <span className="shrink-0 truncate text-fg-muted-regular">
            {ancestors.join(" / ")} /
          </span>
        )}
        <span className="truncate text-fg-regular">{space.name}</span>
        {space.sealed && (
          <Tooltip
            title={"Sealed space\nInvisible without a grant · nothing leaves"}
          >
            <LockOutlinedIcon
              sx={{ fontSize: 11 }}
              className="shrink-0 text-fg-muted"
            />
          </Tooltip>
        )}
        {doomed && (
          <Badge
            variant="warning"
            label={
              space.deletion_started_at
                ? "being deleted"
                : `deleting on ${purgeDate(space.deletion_scheduled_at as string)}`
            }
          />
        )}
        <NorthEastIcon
          aria-hidden
          sx={{ fontSize: 12 }}
          className="shrink-0 text-fg-muted opacity-0 transition-opacity group-hover:opacity-100"
        />
      </button>
      {/* Out of flow until hovered, so the name gives way and no row reserves room. */}
      {(canGrantSelf || doomed) && (
        <span className="hidden shrink-0 items-center group-hover:flex group-has-[:focus-visible]:flex">
          {doomed && !space.deletion_started_at && (
            <TooltipButton
              title="Cancel deletion — the space comes back with its models"
              aria-label={`Cancel deletion of ${space.name}`}
              variant="contextual"
              size="xs"
              loading={isCancelling}
              onClick={(e) => {
                e.stopPropagation();
                onCancelDeletion();
              }}
            >
              <SettingsBackupRestoreIcon fontSize="inherit" />
            </TooltipButton>
          )}
          {canGrantSelf && (
            <TooltipButton
              title="Grant myself manager"
              aria-label={`Grant myself manager of ${space.name}`}
              variant="contextual"
              size="xs"
              loading={isGranting}
              onClick={(e) => {
                e.stopPropagation();
                onGrantSelf();
              }}
            >
              <PersonAddAltOutlinedIcon fontSize="inherit" />
            </TooltipButton>
          )}
        </span>
      )}
      <span className="min-w-0 shrink-0 truncate text-fg-muted-regular">
        {reach ? (
          <Tooltip title={managedByTooltip}>
            <span>
              {roleLabel(reach.role)}
              {reach.via && ` · via ${reach.via}`}
            </span>
          </Tooltip>
        ) : managers.length === 0 ? (
          <Tooltip title="No manager on this space. Its models have no owner until a manager is appointed.">
            <span
              className="flex items-center gap-1"
              style={{ color: "var(--color-warning)" }}
            >
              <PersonOffOutlinedIcon sx={{ fontSize: 12 }} />
              No manager
            </span>
          </Tooltip>
        ) : (
          <Tooltip title={managedByTooltip}>
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate">
                {managers[0].name || holderAddress(managers[0])}
              </span>
              {managers.length > 1 && <span>+{managers.length - 1}</span>}
            </span>
          </Tooltip>
        )}
      </span>
    </div>
  );
};
